// src/lib/coordinateGrid/transformations.ts
/**
 * Transformation Engine
 * 
 * Mathematical functions for geometric transformations.
 * Phase 5: Transformations
 */

import { Point2D, ReflectionLine, TransformParameters, Transformation, ConstructionAid } from '../../types/coordinateGrid';

/**
 * Apply translation to points
 */
export function translate(points: Point2D[], vector: Point2D): Point2D[] {
  return points.map(p => ({
    x: p.x + vector.x,
    y: p.y + vector.y,
  }));
}

/**
 * Apply reflection to points
 */
export function reflect(points: Point2D[], line: ReflectionLine): Point2D[] {
  if ('preset' in line) {
    // Handle preset lines
    switch (line.preset) {
      case 'x-axis':
        // Reflect across x-axis: (x, y) → (x, -y)
        return points.map(p => ({ x: p.x, y: -p.y }));
      
      case 'y-axis':
        // Reflect across y-axis: (x, y) → (-x, y)
        return points.map(p => ({ x: -p.x, y: p.y }));
      
      case 'y=x':
        // Reflect across y=x: (x, y) → (y, x)
        return points.map(p => ({ x: p.y, y: p.x }));
      
      case 'y=-x':
        // Reflect across y=-x: (x, y) → (-y, -x)
        return points.map(p => ({ x: -p.y, y: -p.x }));
      
      default:
        return points;
    }
  } else {
    // Custom line: y = mx + c
    const { m, c } = line.custom;
    
    // Reflection formula for line y = mx + c:
    // For point (x, y), reflected point is:
    // x' = (2m(y - c) + (1 - m²)x) / (1 + m²)
    // y' = (2m(x) + (m² - 1)(y - c)) / (1 + m²) + c
    
    const m2 = m * m;
    const denom = 1 + m2;
    
    return points.map(p => {
      const yMinusC = p.y - c;
      const xNew = (2 * m * yMinusC + (1 - m2) * p.x) / denom;
      const yNew = (2 * m * p.x + (m2 - 1) * yMinusC) / denom + c;
      return { x: xNew, y: yNew };
    });
  }
}

/**
 * Apply rotation to points
 */
export function rotate(
  points: Point2D[], 
  centre: Point2D, 
  angle: number, 
  direction: 'cw' | 'ccw'
): Point2D[] {
  // Convert angle to radians
  let angleRad = (angle * Math.PI) / 180;
  
  // Adjust sign for clockwise/anticlockwise
  // In standard math, positive angles are counterclockwise
  // So clockwise means negative angle
  if (direction === 'cw') {
    angleRad = -angleRad;
  }
  
  const cos = Math.cos(angleRad);
  const sin = Math.sin(angleRad);
  
  // Apply rotation matrix around centre
  return points.map(p => {
    // Translate to origin
    const dx = p.x - centre.x;
    const dy = p.y - centre.y;
    
    // Rotate
    const xNew = dx * cos - dy * sin;
    const yNew = dx * sin + dy * cos;
    
    // Translate back
    return {
      x: centre.x + xNew,
      y: centre.y + yNew,
    };
  });
}

/**
 * Apply enlargement to points
 */
export function enlarge(
  points: Point2D[], 
  centre: Point2D, 
  scaleFactor: number
): Point2D[] {
  return points.map(p => ({
    x: centre.x + scaleFactor * (p.x - centre.x),
    y: centre.y + scaleFactor * (p.y - centre.y),
  }));
}

/**
 * Apply transformation to points based on transformation parameters
 */
export function applyTransformation(
  points: Point2D[],
  parameters: TransformParameters
): Point2D[] {
  switch (parameters.type) {
    case 'translation':
      return translate(points, parameters.vector);
    
    case 'reflection':
      return reflect(points, parameters.line);
    
    case 'rotation':
      return rotate(points, parameters.centre, parameters.angle, parameters.direction);
    
    case 'enlargement':
      return enlarge(points, parameters.centre, parameters.scaleFactor);
    
    default:
      return points;
  }
}

/**
 * Generate construction aid geometry for transformations
 */
export function getConstructionAids(
  transformation: Transformation,
  original: Point2D[],
  transformed: Point2D[]
): ConstructionAid[] {
  const aids: ConstructionAid[] = [];
  
  if (!transformation.showConstruction) {
    return aids;
  }
  
  const { type, parameters } = transformation;
  
  switch (type) {
    case 'translation': {
      // Translation: Arrow from first original point to transformed point
      if (original.length > 0 && transformed.length > 0) {
        aids.push({
          type: 'arrow',
          start: original[0],
          end: transformed[0],
          color: transformation.imageColor,
          strokeStyle: 'dashed',
          strokeWidth: 2,
        });
      }
      break;
    }
    
    case 'reflection': {
      // Reflection: Perpendicular lines from points to mirror line
      if ('preset' in parameters.line) {
        // For preset lines, draw the mirror line
        const preset = parameters.line.preset;
        let lineStart: Point2D;
        let lineEnd: Point2D;
        
        // Use viewport bounds (approximate - would need actual viewport)
        const bounds = 20;
        
        switch (preset) {
          case 'x-axis':
            lineStart = { x: -bounds, y: 0 };
            lineEnd = { x: bounds, y: 0 };
            break;
          case 'y-axis':
            lineStart = { x: 0, y: -bounds };
            lineEnd = { x: 0, y: bounds };
            break;
          case 'y=x':
            lineStart = { x: -bounds, y: -bounds };
            lineEnd = { x: bounds, y: bounds };
            break;
          case 'y=-x':
            lineStart = { x: bounds, y: -bounds };
            lineEnd = { x: -bounds, y: bounds };
            break;
        }
        
        aids.push({
          type: 'line',
          start: lineStart,
          end: lineEnd,
          color: '#9CA3AF',
          strokeStyle: 'dashed',
          strokeWidth: 1,
        });
      } else {
        // For custom line, draw the line
        const { m, c } = parameters.line.custom;
        const bounds = 20;
        const x1 = -bounds;
        const y1 = m * x1 + c;
        const x2 = bounds;
        const y2 = m * x2 + c;
        
        aids.push({
          type: 'line',
          start: { x: x1, y: y1 },
          end: { x: x2, y: y2 },
          color: '#9CA3AF',
          strokeStyle: 'dashed',
          strokeWidth: 1,
        });
      }
      
      // Draw perpendicular lines from original to transformed points
      for (let i = 0; i < Math.min(original.length, transformed.length); i++) {
        aids.push({
          type: 'line',
          start: original[i],
          end: transformed[i],
          color: transformation.imageColor,
          strokeStyle: 'dotted',
          strokeWidth: 1,
        });
      }
      break;
    }
    
    case 'rotation': {
      // Rotation: Arc showing angle, radius lines from centre
      const { centre, angle } = parameters;
      
      // Find a point to determine radius
      if (original.length > 0) {
        const firstPoint = original[0];
        const transformedPoint = transformed[0];
        const radius = Math.sqrt(
          Math.pow(firstPoint.x - centre.x, 2) + 
          Math.pow(firstPoint.y - centre.y, 2)
        );
        
        // Calculate angles
        const startAngle = Math.atan2(
          firstPoint.y - centre.y,
          firstPoint.x - centre.x
        );
        const endAngle = startAngle + (parameters.direction === 'cw' ? -angle : angle) * Math.PI / 180;
        
        aids.push({
          type: 'arc',
          centre,
          radius,
          startAngle: startAngle * 180 / Math.PI,
          endAngle: endAngle * 180 / Math.PI,
          color: transformation.imageColor,
          strokeStyle: 'solid',
          strokeWidth: 2,
        });
        
        // Draw radius lines
        aids.push({
          type: 'line',
          start: centre,
          end: firstPoint,
          color: '#9CA3AF',
          strokeStyle: 'dashed',
          strokeWidth: 1,
        });
        aids.push({
          type: 'line',
          start: centre,
          end: transformedPoint,
          color: transformation.imageColor,
          strokeStyle: 'dashed',
          strokeWidth: 1,
        });
      }
      break;
    }
    
    case 'enlargement': {
      // Enlargement: Ray lines from centre through vertices
      const { centre } = parameters;
      
      for (let i = 0; i < original.length; i++) {
        aids.push({
          type: 'line',
          start: centre,
          end: transformed[i],
          color: transformation.imageColor,
          strokeStyle: 'dashed',
          strokeWidth: 1,
        });
      }
      break;
    }
  }
  
  return aids;
}

/**
 * Calculate the centroid of a set of points
 */
export function calculateCentroid(points: Point2D[]): Point2D {
  if (points.length === 0) {
    return { x: 0, y: 0 };
  }
  
  const sum = points.reduce(
    (acc, p) => ({ x: acc.x + p.x, y: acc.y + p.y }),
    { x: 0, y: 0 }
  );
  
  return {
    x: sum.x / points.length,
    y: sum.y / points.length,
  };
}
